import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../context/LanguageContext';
import { PageContainer, PageTitle } from '../../../components/Common';
import ConfirmDialog from '../../Clients/ListImport/components/ConfirmDialog';
import GlobalSettingsCard from './components/GlobalSettingsCard';
import TimeslotsTableRow from './components/TimeslotsTableRow';
import useGetDefaultTimeslots from '../../../hooks/Marketing/Timeslots/useGetDefaultTimeslots';
import useGetAllTimeslots from '../../../hooks/Marketing/Timeslots/useGetAllTimeslots';
import useEntityTimeslotMutations from '../../../hooks/Marketing/Timeslots/useEntityTimeslotMutations';
import { CHANNEL_IDS, ChannelType, SearchFieldType } from './types';

const INITIAL_PAGE = 0;
const DEFAULT_ROWS_PER_PAGE = 10;

const SEARCH_FIELD_OPTIONS: SearchFieldType[] = ['entity_id', 'entity_identifier', 'entity_name'];

const Timeslots = () => {
    const { t } = useLanguage();
    const { data: defaultTimeslots, loading: loadingDefaults } = useGetDefaultTimeslots();
    const { getAllTimeslots, loading: loadingTimeslots, data: timeslotsData, isReady } = useGetAllTimeslots();
    const { createEntityTimeslot, updateEntityTimeslot, deleteEntityTimeslot } = useEntityTimeslotMutations();

    const [page, setPage] = useState(INITIAL_PAGE);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [searchTerm, setSearchTerm] = useState('');
    const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
    const [searchField, setSearchField] = useState<SearchFieldType>('entity_name');
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const rows = useMemo(() => timeslotsData?.member ?? [], [timeslotsData]);
    const totalItems = timeslotsData?.totalItems ?? 0;

    const getAllTimeslotsRef = useRef(getAllTimeslots);
    getAllTimeslotsRef.current = getAllTimeslots;

    useEffect(() => {
        if (!isReady) return;
        getAllTimeslotsRef.current({ page: page + 1, itemsPerPage: rowsPerPage, search: submittedSearchTerm, searchField });
    }, [isReady, page, rowsPerPage, submittedSearchTerm, searchField]);

    const refreshTable = useCallback(() => {
        getAllTimeslots({ page: page + 1, itemsPerPage: rowsPerPage, search: submittedSearchTerm, searchField });
    }, [getAllTimeslots, page, rowsPerPage, submittedSearchTerm, searchField]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(INITIAL_PAGE);
    }, []);

    const handleSearchSubmit = useCallback(() => {
        const normalizedSearch = searchTerm.trim();
        setPage(INITIAL_PAGE);
        if (normalizedSearch === submittedSearchTerm && page === INITIAL_PAGE) {
            refreshTable();
            return;
        }
        setSubmittedSearchTerm(normalizedSearch);
    }, [searchTerm, submittedSearchTerm, page, refreshTable]);

    const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    }, [handleSearchSubmit]);

    const handleSearchFieldChange = useCallback((event: SelectChangeEvent) => {
        setSearchField(event.target.value as SearchFieldType);
        setPage(INITIAL_PAGE);
    }, []);

    const handleSaveTimeslot = useCallback(async (
        entityId: number,
        channel: ChannelType,
        timeslotId: number | null,
        startHour: string,
        endHour: string,
    ): Promise<boolean> => {
        const result = timeslotId != null
            ? await updateEntityTimeslot({ timeslotId, startHour, endHour })
            : await createEntityTimeslot({ entityId, channelId: CHANNEL_IDS[channel], startHour, endHour });

        if (result) {
            toast.success(t('pages.marketing.timeslotsPage.saveSuccess'));
            refreshTable();
            return true;
        }
        toast.error(t('pages.marketing.timeslotsPage.saveError'));
        return false;
    }, [createEntityTimeslot, updateEntityTimeslot, refreshTable, t]);

    const handleRequestReset = useCallback((entityId: number, channel: ChannelType, timeslotId: number) => {
        setConfirmDialog({
            open: true,
            title: t('pages.marketing.timeslotsPage.confirmResetTitle'),
            message: t('pages.marketing.timeslotsPage.confirmResetMessage'),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                const success = await deleteEntityTimeslot(timeslotId);
                if (success) {
                    toast.success(t('pages.marketing.timeslotsPage.resetSuccess'));
                    refreshTable();
                } else {
                    toast.error(t('pages.marketing.timeslotsPage.resetError'));
                }
            },
        });
    }, [deleteEntityTimeslot, refreshTable, t]);

    return (
        <PageContainer>
            <PageTitle>{t('pages.marketing.timeslots')}</PageTitle>

            <GlobalSettingsCard data={defaultTimeslots} loading={loadingDefaults} />

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <Typography variant="subtitle1">
                            {t('pages.marketing.timeslotsPage.definedTimeslotsTitle')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Select
                                size="small"
                                value={searchField}
                                onChange={handleSearchFieldChange}
                                sx={{ minWidth: 150 }}
                            >
                                {SEARCH_FIELD_OPTIONS.map(field => (
                                    <MenuItem key={field} value={field}>
                                        {t(`pages.marketing.timeslotsPage.searchField.${field}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <TextField
                                size="small"
                                placeholder={t('pages.marketing.timeslotsPage.searchPlaceholder')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                sx={{ minWidth: 220 }}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                                }}
                            />
                        </Box>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('pages.marketing.timeslotsPage.columnStoreId')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('pages.marketing.timeslotsPage.columnStoreCode')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('pages.marketing.timeslotsPage.columnStore')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('pages.marketing.timeslotsPage.columnSms')}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('pages.marketing.timeslotsPage.columnEmail')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadingTimeslots ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                            <Typography color="text.secondary">
                                                {t('pages.marketing.timeslotsPage.noStores')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map(row => (
                                        <TimeslotsTableRow
                                            key={row.entity_id}
                                            row={row}
                                            onSave={handleSaveTimeslot}
                                            onRequestReset={handleRequestReset}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('common.rowsPerPage')}
                        sx={{ marginLeft: 'auto' }}
                    />
                </CardContent>
            </Card>

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                confirmText={t('common.confirm')}
                cancelText={t('common.cancel')}
                confirmColor="warning"
            />
        </PageContainer>
    );
};

export default Timeslots;
